/* eslint-disable react-hooks/exhaustive-deps */
// import { Download, Printer } from 'lucide-react';
import { useContext } from 'react';
import moment from 'moment'
import { ProjectContext } from '../App';
import { useGetInvoice } from '../hooks/useGetInvoice';
import { useParams } from 'react-router';
import { currencyFormatter } from '../utils/formatter';
import NotFound from './NotFound';
import { Loading } from './Loading';
import { Service, ServiceBookingItem } from '../types/service.type';
import { OrderDetail } from '../types/order.type';

export default function InvoicePage() {

  const { project } = useContext(ProjectContext)

  const { shortUrlCode } = useParams()
  const { invoice, booking, loading, order, rental } = useGetInvoice(shortUrlCode!)

  if (loading) {
    return <Loading />
  }

  if (
    !loading &&
    (!invoice ||
      (invoice?.sourceType === "booking" && !booking) ||
      (invoice?.sourceType === "order" && !order) ||
      (invoice?.sourceType === "rental" && !rental))
  ) {
    return <NotFound />
  }

  // Calculate totals
  const bookingItems = booking?.serviceBookingItems || booking?.serviceJson || []
  const orderItems = order?.orderDetails || []
  const rentalItems = rental?.rentalDetails || []
  const bookingSubtotal =
    (bookingItems as (ServiceBookingItem | Service)[])
      ?.reduce((acc: number, item: ServiceBookingItem | Service) => {
        if ('subTotal' in item) {
          return acc + item.subTotal
        }
        return acc + item.price
      }, 0) || 0;

  const orderSubtotal =
    (orderItems as OrderDetail[])
      ?.reduce((acc: number, item: OrderDetail) => {
        return acc + item.subtotal
      }, 0) || 0;

  const rentalSubtotal = rentalItems.reduce((acc, item) => acc + item.subtotal, 0);
  const rentalSubtotalForTotals =
    invoice?.sourceType === "rental" && invoice != null
      ? Number(invoice.subtotal)
      : rentalSubtotal

  const total = bookingSubtotal + orderSubtotal + rentalSubtotalForTotals;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            {project?.logoImageFiles?.[0]?.s3Url && (
              <img
                src={project.logoImageFiles[0].s3Url}
                alt="Company Logo"
                className="w-32 h-32 object-contain mb-4"
              />
            )}
            <h1 className="text-3xl font-bold text-gray-800">INVOICE</h1>
            <p className="text-gray-600">#{invoice?.invoiceNumber}</p>
          </div>
          {/* <div className="flex gap-4">
            <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
              <Printer className="w-4 h-4" />
              Print
            </button>
            <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
              <Download className="w-4 h-4" />
              Download
            </button>
          </div> */}
        </div>

        {/* Company and Client Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Company Details */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">From:</h2>
            <div className="text-gray-600">
              <p className="font-medium">{project?.name}</p>
              <p>{project?.address}</p>
              <p className="mt-2">{project?.contactEmail}</p>
              <p>{project?.contactNo}</p>
            </div>
          </div>

          {/* Client Details */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Bill To:</h2>
            <div className="text-gray-600 break-words">
              <p className="font-medium">
                {booking?.customer?.name || order?.customer?.name || rental?.customer?.name}
              </p>
              <p>{booking?.customer?.address || order?.customer?.address || rental?.customer?.address}</p>
              <p className="mt-2">
                {booking?.customer?.email || order?.customer?.email || rental?.customer?.email}
              </p>
              <p>{booking?.customer?.phoneNo || order?.customer?.phoneNo || rental?.customer?.phoneNo}</p>
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <p className="text-gray-600">
              <span className="font-medium">Invoice Date:</span> {moment(invoice?.createdAt).format('DD/MM/YYYY')}
            </p>
            {/* <p className="text-gray-600">
              <span className="font-medium">Due Date:</span> {booking?.reservationEndDateTime?.toLocaleDateString()}
            </p> */}
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-8">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Description</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Quantity</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoice?.sourceType === 'booking' && (
                bookingItems
                  .map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {'title' in item ? item.title : item.serviceObject?.title}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right">{1}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right">{currencyFormatter(item.price)}</td>
                    </tr>
                  ))
              )}
              {invoice?.sourceType === 'order' && (
                orderItems
                  .map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.productName}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right">{item.quantity}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right">{currencyFormatter(item.subtotal)}</td>
                    </tr>
                  ))
              )}
              {invoice?.sourceType === "rental" &&
                rentalItems.map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.productName}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">{item.quantity}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">
                      {currencyFormatter(invoice.subtotal ?? item.subtotal)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-64">
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Subtotal:</span>
              <span className="px-4 py-3 text-gray-800">
                {currencyFormatter(
                  bookingSubtotal || orderSubtotal || rentalSubtotalForTotals
                )}
              </span>
            </div>
            <div className="flex justify-between py-2 border-t border-gray-200">
              <span className="font-medium text-gray-800">Total:</span>
              <span className="px-4 py-3 font-medium text-gray-800">{currencyFormatter(total)}</span>
            </div>
          </div>
        </div>

        {/* Note */}
        {
          (invoice?.notes || project?.metadata?.invoiceNote) && (
            <div className="mt-8 p-6 bg-gray-50 rounded-lg text-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Note:</h3>
              <div
                className="text-gray-600 space-y-4"
                dangerouslySetInnerHTML={{ __html: invoice?.notes || project?.metadata?.invoiceNote || '' }}>
              </div>
            </div>
          )
        }

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-600 text-center">
            Thank you for your business!
          </p>
        </div>
      </div>
    </div>
  );
}